import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import FieldsList from "./FieldsList";
import IgnoredTray from "./IgnoredTray";
import { IgnoreButton, ignoreItem } from "./IgnoreButton";

export default function PhoneSection({ onChange }) {
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(null); // {phone, records}
  const [primaryId, setPrimaryId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ignoreVersion, setIgnoreVersion] = useState(0);
  // Bulk-select state
  const [checked, setChecked] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });

  async function load() {
    const { data } = await api.get(`/cleanup/phone-groups`);
    setGroups(data.groups || []);
    setChecked(new Set()); // clear selections on reload
  }
  useEffect(() => { load(); }, []);

  function openGroup(g) {
    setSelected(g);
    // Suggest primary: prefer realtor_ca source, else first
    const realtorCa = g.records.find((r) => r.source === "realtor_ca");
    setPrimaryId(realtorCa?.id || g.records[0]?.id || "");
    setPreview(null);
  }

  function toggleCheck(phone) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  function toggleAll() {
    if (!groups) return;
    setChecked((prev) => {
      if (prev.size === groups.length) return new Set();
      return new Set(groups.map((g) => g.phone));
    });
  }

  async function computePreview() {
    if (!selected || !primaryId) return;
    const sec = selected.records.filter((r) => r.id !== primaryId).map((r) => r.id);
    if (sec.length === 0) { toast.error("Pick a different primary; no secondaries left"); return; }
    try {
      const { data } = await api.post(`/cleanup/phone-groups/preview`, {
        primary_id: primaryId, secondary_ids: sec,
      });
      setPreview(data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  }

  async function applyMerge() {
    if (!selected || !primaryId) return;
    const sec = selected.records.filter((r) => r.id !== primaryId).map((r) => r.id);
    setBusy(true);
    try {
      const { data } = await api.post(`/cleanup/phone-dedupe`, {
        primary_id: primaryId, secondary_ids: sec,
      });
      toast.success(`Merged ${data.deleted} secondaries into primary`);
      setSelected(null); setPreview(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  }

  // Reject-and-delete a single record from the dedupe tray without merging.
  // Used when the "duplicate" is really an open-house scrape that should be
  // trashed while leaving the completed profile intact.
  async function rejectRecord(record) {
    if (!record?.id) return;
    setBusy(true);
    try {
      await api.delete(`/cleanup/realtor/${record.id}`);
      toast.success(`Deleted ${record.name || "record"}`, {
        description: "Moved to Recycle Bin. Merge dialog updated.",
      });
      // Prune from the currently-open group + reload the groups list.
      setSelected((cur) =>
        cur
          ? { ...cur, records: cur.records.filter((r) => r.id !== record.id) }
          : cur,
      );
      if (primaryId === record.id) setPrimaryId("");
      setPreview(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  }

  // Pick the primary for bulk merge using the SAME logic as the inspect dialog:
  // prefer source=realtor_ca, else the first record.
  function pickDefaultPrimary(records) {
    const realtorCa = records.find((r) => r.source === "realtor_ca");
    return realtorCa || records[0];
  }

  async function afterRestore() {
    await load();
    setIgnoreVersion((v) => v + 1);
    onChange?.();
  }

  async function ignoreGroup(g) {
    setBusy(true);
    try {
      await ignoreItem(
        { kind: "phone_group", key: g.phone, member_ids: g.records.map((r) => r.id) },
        { label: g.phone, onUndo: afterRestore },
      );
      setGroups((gs) => (gs || []).filter((x) => x.phone !== g.phone));
      setChecked((prev) => { const next = new Set(prev); next.delete(g.phone); return next; });
      setIgnoreVersion((v) => v + 1);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ignore failed");
    } finally { setBusy(false); }
  }

  async function applyBulk() {
    if (!groups || checked.size === 0) return;
    setBulkBusy(true);
    const targets = groups.filter((g) => checked.has(g.phone));
    let done = 0, errors = 0;
    setBulkProgress({ done: 0, total: targets.length, errors: 0 });
    for (const g of targets) {
      const primary = pickDefaultPrimary(g.records);
      const secondaryIds = g.records.filter((r) => r.id !== primary.id).map((r) => r.id);
      if (secondaryIds.length === 0) { done++; setBulkProgress({ done, total: targets.length, errors }); continue; }
      try {
        await api.post(`/cleanup/phone-dedupe`, {
          primary_id: primary.id, secondary_ids: secondaryIds,
        });
      } catch (e) { errors++; }
      done++;
      setBulkProgress({ done, total: targets.length, errors });
    }
    setBulkBusy(false);
    setBulkConfirmOpen(false);
    toast.success(`Merged ${done - errors} of ${targets.length} groups${errors ? `, ${errors} failed` : ""}`);
    await load();
    onChange?.();
  }

  if (groups === null) return <div className="text-sm text-slate-500">Loading…</div>;
  if (groups.length === 0) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-slate-500">No phone duplicates.</div>
        <IgnoredTray kind="phone_group" version={ignoreVersion} onRestored={afterRestore} />
      </div>
    );
  }

  // Master checkbox state — radix `checked` accepts true | false | "indeterminate"
  const allChecked = groups.length > 0 && checked.size === groups.length;
  const someChecked = checked.size > 0 && !allChecked;
  const masterState = allChecked ? true : someChecked ? "indeterminate" : false;
  // Sum of realtors across selected groups (for the confirm copy)
  const selectedRealtorCount = groups
    .filter((g) => checked.has(g.phone))
    .reduce((sum, g) => sum + g.records.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={masterState}
              onCheckedChange={toggleAll}
              data-testid="phone-select-all"
              aria-label="Select all phone-dupe groups"
            />
            <span className="text-sm text-slate-700">
              Select all ({checked.size}/{groups.length})
            </span>
          </label>
        </div>
        <Button
          size="sm"
          disabled={checked.size === 0 || bulkBusy}
          onClick={() => setBulkConfirmOpen(true)}
          data-testid="phone-apply-checked"
        >
          Apply to Checked ({checked.size})
        </Button>
      </div>

      <div className="text-xs text-slate-500">
        Click a group to inspect / pick a different primary, or use checkboxes for bulk default-merge.
      </div>

      <div className="grid gap-2 max-h-[500px] overflow-y-auto">
        {groups.map((g) => {
          const isChecked = checked.has(g.phone);
          return (
            <div
              key={g.phone}
              className={`rounded-md border bg-white p-3 flex items-center gap-3 min-h-[60px] transition ${
                isChecked ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"
              }`}
              data-testid={`phone-group-row-${g.phone}`}
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => toggleCheck(g.phone)}
                data-testid={`phone-group-checkbox-${g.phone}`}
                aria-label={`Select phone group ${g.phone}`}
              />
              <button
                type="button"
                onClick={() => openGroup(g)}
                className="flex-1 text-left flex items-center justify-between gap-3 hover:bg-slate-50/60 -m-1 p-1 rounded transition"
                data-testid={`phone-group-${g.phone}`}
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm text-slate-700">{g.phone}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {g.records.map((r) => `${r.name} (${r.source})`).join(" · ")}
                  </div>
                </div>
                <Badge variant="secondary">{g.records.length}</Badge>
              </button>
              <IgnoreButton
                onClick={() => ignoreGroup(g)}
                disabled={busy}
                testId={`phone-group-ignore-${g.phone}`}
              />
            </div>
          );
        })}
      </div>

      <IgnoredTray kind="phone_group" version={ignoreVersion} onRestored={afterRestore} />

      {/* Bulk-merge confirm dialog */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={(open) => { if (!open && !bulkBusy) setBulkConfirmOpen(false); }}>
        <AlertDialogContent data-testid="phone-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply default merge to {checked.size} group{checked.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will dedupe <strong>{selectedRealtorCount} realtors</strong> across{" "}
              <strong>{checked.size} group{checked.size === 1 ? "" : "s"}</strong>. For each group, the
              record sourced from <code>realtor_ca</code> wins as the primary (or the first record if
              none is from realtor.ca). Secondaries' missing fields are filled in, photo prefers
              realtor.ca, notes are concatenated, socials are unioned, then duplicates are deleted.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {bulkBusy && (
            <div className="text-sm text-slate-600" data-testid="phone-bulk-progress">
              Merging group {bulkProgress.done} of {bulkProgress.total}…
              {bulkProgress.errors > 0 && <span className="text-rose-600 ml-2">({bulkProgress.errors} failed)</span>}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkBusy}
              onClick={applyBulk}
              data-testid="phone-bulk-confirm-apply"
            >
              {bulkBusy ? "Merging…" : `Apply ${checked.size} merges`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setPreview(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Merge phone-duplicate group</DialogTitle>
            <DialogDescription>
              Select the primary record. Secondaries will be merged into it (notes concatenated, photo from realtor.ca preferred, socials unioned), then deleted.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {selected.records.map((r) => {
                  const isPrimary = r.id === primaryId;
                  return (
                    <div
                      key={r.id}
                      className={`relative text-left p-3 rounded-md border transition ${
                        isPrimary ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                      data-testid={`phone-merge-candidate-${r.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => { setPrimaryId(r.id); setPreview(null); }}
                        className="absolute inset-0 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        aria-label={isPrimary ? "Selected as primary" : `Set ${r.name} as primary`}
                        data-testid={`phone-merge-set-primary-${r.id}`}
                      />
                      <div className="relative flex items-center justify-between gap-2 pointer-events-none">
                        <span className="font-medium text-slate-900 truncate">{r.name}</span>
                        <div className="flex items-center gap-1 pointer-events-auto">
                          {isPrimary && <Badge className="bg-emerald-600">PRIMARY</Badge>}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  `Delete "${r.name}" and remove it from the merge tray? It will move to the Recycle Bin. Other records here are untouched.`,
                                )
                              ) {
                                rejectRecord(r);
                              }
                            }}
                            className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300"
                            title="Reject &amp; delete this row (won't touch the primary)"
                            data-testid={`phone-merge-reject-${r.id}`}
                            disabled={busy}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="relative pointer-events-none">
                        <FieldsList r={r} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                <Button variant="outline" onClick={computePreview} data-testid="phone-merge-preview-btn">
                  Preview merge
                </Button>
                {preview && (
                  <div className="text-xs text-slate-500 truncate">
                    Merged record will keep: {Object.entries(preview).filter(([k,v]) => v && typeof v === 'string').slice(0,3).map(([k,v]) => `${k}=${(""+v).slice(0,30)}`).join(", ")}…
                  </div>
                )}
              </div>

              {preview && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-medium text-emerald-700 mb-2">MERGE PREVIEW</div>
                  <FieldsList r={preview} />
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => { setSelected(null); setPreview(null); }}>Cancel</Button>
                <Button
                  onClick={applyMerge}
                  disabled={busy || !primaryId}
                  data-testid="phone-merge-apply"
                >
                  {busy ? "Merging…" : "Apply merge"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
